import { useState, useEffect, lazy, Suspense } from 'react';
import { useCampaign } from '../../hooks/queries/useCampaigns';
import { useCampaign as useCampaignContext } from '../../context/CampaignContext';
import CampaignIdentityBlock from '../../components/campaign/CampaignIdentityBlock';

// Lazy load heavy tab components for better performance
import CampaignManager from '../../apps/recruiter/components/Campaign/CampaignManager';
import AdvertisementManager from '../../apps/recruiter/components/Advertisement/AdvertisementManager';
import AdSourcesPanel from '../../apps/recruiter/components/Sources/AdSourcesPanel';
import ReviewPanel from '../../apps/recruiter/components/Review/ReviewPanel';
const MobilePreviewDashboard = lazy(() => import('../../components/campaign/MobilePreviewDashboard'));

interface CampaignDetailViewProps {
  campaignId: string;
}

type TabId = 'settings' | 'posting' | 'sources' | 'preview';

const TABS: { id: TabId; label: string | JSX.Element }[] = [
  { id: 'settings', label: 'Settings' },
  { id: 'posting', label: 'Posting' },
  { id: 'sources', label: 'Sources' },
  {
    id: 'preview',
    label: 'Screens'
  },
];

interface JobFormData {
  role: string;
  completed: boolean;
  data: any;
}

export default function CampaignDetailView({ campaignId }: CampaignDetailViewProps) {
  // Initialize active tab from URL query param if present
  const getInitialTab = (): TabId => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab') as TabId | null;
    if (tabParam && ['settings', 'posting', 'sources', 'preview'].includes(tabParam)) {
      return tabParam;
    }
    return 'settings';
  };

  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab());
  const { setCampaign: setGlobalCampaign, selectedJobRole, selectedLocations: globalLocations } = useCampaignContext();

  // State for CampaignManager
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

  // State for AdvertisementManager (Posting tab)
  const [jobForms, setJobForms] = useState<JobFormData[]>([]);

  const { data: campaign, isLoading, isError } = useCampaign(campaignId);

  // Sync campaign data into global context and local state when loaded
  useEffect(() => {
    if (!campaign) return;

    const newLocation = campaign.location;
    const newRole = campaign.role;
    const currentLocation = globalLocations[0];

    if (currentLocation !== newLocation || selectedJobRole !== newRole) {
      setGlobalCampaign([newLocation], newRole);
    }

    setSelectedLocations([campaign.location]);
    setSelectedJobs([campaign.role]);
    setJobForms([{ role: campaign.role, completed: false, data: null }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.campaignId]);

  // Listen for tour tab changes
  useEffect(() => {
    const handleTourTabChange = (event: CustomEvent<{ tab: string }>) => {
      const tab = event.detail.tab as TabId;
      if (['settings', 'posting', 'sources', 'preview'].includes(tab)) {
        setActiveTab(tab);
      }
    };

    // Listen for URL query param changes (from tour navigation)
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab') as TabId | null;
      if (tabParam && ['settings', 'posting', 'sources', 'preview'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    };

    window.addEventListener('tour-tab-change', handleTourTabChange as EventListener);
    window.addEventListener('popstate', handleUrlChange);

    // Check URL on mount
    handleUrlChange();

    return () => {
      window.removeEventListener('tour-tab-change', handleTourTabChange as EventListener);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
        <span className="text-sm text-gray-500">Loading campaign...</span>
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Campaign not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Tab Bar with Campaign Context */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            {/* Left: Tabs */}
            <div className="flex gap-1 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#0156FF] bg-[#0156FF] text-white rounded-t-lg'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Right: Campaign Context */}
            <div className="py-4">
              <CampaignIdentityBlock
                campaignName={campaign.name}
                role={campaign.role}
                locations={campaign.location}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        <Suspense fallback={
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          </div>
        }>
          {activeTab === 'settings' && (
            <CampaignManager
              selectedLocations={selectedLocations}
              setSelectedLocations={setSelectedLocations}
              selectedJobs={selectedJobs}
              setSelectedJobs={setSelectedJobs}
              onOpenPlanner={() => {}}
              onOpenSources={() => setActiveTab('sources')}
            />
          )}

          {activeTab === 'posting' && (
            <div className="bg-gray-50">
              <AdvertisementManager
                selectedJobs={selectedJobs}
                selectedLocations={selectedLocations}
                jobForms={jobForms}
                setJobForms={setJobForms}
              />
            </div>
          )}

          {activeTab === 'sources' && (
            <div className="mx-auto px-6 py-8 space-y-6" style={{ maxWidth: '1310px' }}>
              <ReviewPanel selectedJobs={selectedJobs} selectedLocations={selectedLocations} />
              <AdSourcesPanel campaignId={campaignId} />
            </div>
          )}

          {activeTab === 'preview' && <MobilePreviewDashboard campaignId={campaignId} />}
        </Suspense>
      </div>
    </div>
  );
}
